import { useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { GAME_CONFIG, type GameMode, type RoomState } from '@majority-report/shared';
import { useGame } from '../net/GameProvider';
import { buildRoomLink } from '../net/joinLink';
import { copyText } from '../lib/clipboard';
import { PlayerChip } from '../components/PlayerChip';
import { Button, Card, Heading, Pill, Row, Stack, Subtle } from '../components/ui';
import { shimmer } from '../styles/GlobalStyle';

const CodeCard = styled(Card)`
  text-align: center;
  background: linear-gradient(120deg, #fff0f8, #f2f0ff 50%, #eaf6ff);
  background-size: 220% 220%;
  animation: ${shimmer} 9s ease infinite;
`;

const Code = styled.div`
  font-size: clamp(2.6rem, 13vw, 4.2rem);
  font-weight: 800;
  letter-spacing: 0.22em;
  text-indent: 0.22em;
  color: ${({ theme }) => theme.colors.ink};
  line-height: 1.1;
`;

const PlayerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 10px;
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
`;

const OptionCard = styled.button<{ $active: boolean; $locked: boolean }>`
  text-align: left;
  padding: 16px 18px;
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.cream};
  border: 3px solid ${({ theme }) => theme.colors.surfaceSunken};
  transition: all 220ms ${({ theme }) => theme.transitions.bounce};

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.surfaceSoft};
      border-color: ${theme.colors.bubblegumDeep};
      box-shadow: 0 0 0 5px rgba(255, 179, 209, 0.28);
      transform: translateY(-2px);
    `}

  ${({ $locked }) =>
    $locked
      ? css`
          cursor: default;
        `
      : css`
          &:hover {
            transform: translateY(-3px);
          }
        `}
`;

/* A <span>, not a <div>: this renders inside a <button>, which may only
   contain phrasing content. */
const OptionName = styled.span`
  font-weight: 800;
  font-size: 1.05rem;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** Segmented control for the small, mutually exclusive choices. */
const Segmented = styled.div`
  display: inline-flex;
  gap: 6px;
  padding: 5px;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.surfaceSunken};
`;

const Segment = styled.button<{ $active: boolean }>`
  padding: 9px 20px;
  border-radius: ${({ theme }) => theme.radii.pill};
  font-weight: 800;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.inkSoft};
  transition: all 200ms ${({ theme }) => theme.transitions.bounce};

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.surface};
      color: ${theme.colors.ink};
      box-shadow: ${theme.shadows.puff};
      transform: translateY(-1px);
    `}

  &:disabled {
    cursor: default;
  }
`;

const MODES: { mode: GameMode; emoji: string; nameKey: string; hintKey: string }[] = [
  { mode: 'minority-loses', emoji: '🐑', nameKey: 'lobby.modeMinority', hintKey: 'lobby.modeMinorityHint' },
  { mode: 'majority-loses', emoji: '🦄', nameKey: 'lobby.modeMajority', hintKey: 'lobby.modeMajorityHint' },
];

export function LobbyScreen({ state }: { state: RoomState }) {
  const { t } = useTranslation();
  const { actions, playerId, isHost } = useGame();
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const { settings } = state;
  const enough = state.players.length >= GAME_CONFIG.minPlayers;

  /**
   * Hand over a link instead of a code. Uses the native share sheet where there
   * is one (phones), which is how most people will actually pass this around,
   * and falls back to the clipboard everywhere else.
   */
  const shareLink = async () => {
    const url = buildRoomLink(state.code);
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Majority Report', url });
        return;
      } catch {
        /* dismissed, or unsupported after all — fall through to copying. */
      }
    }
    // Only claim success if it actually copied — a silent no-op would leave the
    // host thinking they had a link on the clipboard when they did not.
    if (await copyText(url)) {
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 1800);
    }
  };

  const copyCode = async () => {
    if (await copyText(state.code)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <>
      <CodeCard>
        <Stack $gap="10px" $align="center">
          <Subtle>{t('lobby.codeLabel')}</Subtle>
          <Code>{state.code}</Code>
          <Row $gap="8px" $wrap $justify="center">
            <Button type="button" $tone="primary" $size="sm" onClick={() => void shareLink()}>
              {linkCopied ? `✅ ${t('common.copied')}` : `🔗 ${t('lobby.shareLink')}`}
            </Button>
            <Button type="button" $tone="ghost" $size="sm" onClick={() => void copyCode()}>
              {copied ? `✅ ${t('common.copied')}` : `📋 ${t('common.copy')}`}
            </Button>
          </Row>
          <Subtle $center>{t('lobby.shareHint')}</Subtle>
        </Stack>
      </CodeCard>

      <Card>
        <Stack $gap="14px">
          <Row $justify="space-between" $wrap $gap="8px">
            <Heading>{t('lobby.playersTitle')}</Heading>
            <Pill $tone="mint">{t('lobby.playerCount', { count: state.players.length })}</Pill>
          </Row>
          <PlayerGrid>
            {state.players.map((player) => (
              <PlayerChip
                key={player.id}
                player={player}
                isYou={player.id === playerId}
                isHost={player.id === state.hostId}
                maxHearts={state.settings.startingHearts}
              />
            ))}
          </PlayerGrid>
          {!enough && <Subtle>{t('lobby.needMorePlayers', { count: GAME_CONFIG.minPlayers })}</Subtle>}
        </Stack>
      </Card>

      <Card>
        <Stack $gap="22px">
          <Row $justify="space-between" $wrap $gap="8px">
            <Heading>{t('lobby.settingsTitle')}</Heading>
            {!isHost && <Pill $tone="lavender">🔒 {t('lobby.settingsLockedHint')}</Pill>}
          </Row>

          {/* Who loses a heart */}
          <Stack $gap="12px">
            <Subtle>{t('lobby.modeTitle')}</Subtle>
            <ModeGrid>
              {MODES.map(({ mode, emoji, nameKey, hintKey }) => (
                <OptionCard
                  key={mode}
                  type="button"
                  $active={settings.mode === mode}
                  $locked={!isHost}
                  aria-label={`${t(nameKey)} — ${t(hintKey)}`}
                  aria-pressed={settings.mode === mode}
                  disabled={!isHost}
                  onClick={() => isHost && actions.setMode(mode)}
                >
                  <OptionName>
                    <span aria-hidden="true">{emoji}</span>
                    {t(nameKey)}
                  </OptionName>
                  <Subtle as="span">{t(hintKey)}</Subtle>
                </OptionCard>
              ))}
            </ModeGrid>
          </Stack>

          {/* Starting hearts */}
          <Stack $gap="10px">
            <Subtle>{t('lobby.heartsTitle')}</Subtle>
            <Segmented role="group" aria-label={t('lobby.heartsTitle')}>
              {GAME_CONFIG.heartOptions.map((count) => (
                <Segment
                  key={count}
                  type="button"
                  $active={settings.startingHearts === count}
                  aria-pressed={settings.startingHearts === count}
                  aria-label={t('lobby.heartsOption', { count })}
                  disabled={!isHost}
                  onClick={() => isHost && actions.setHearts(count)}
                >
                  {'💗'.repeat(count)}
                </Segment>
              ))}
            </Segmented>
            <Subtle>{t('lobby.heartsHint')}</Subtle>
          </Stack>

          {/* Player-written questions */}
          <Stack $gap="10px">
            <Subtle>{t('lobby.customTitle')}</Subtle>
            <Segmented role="group" aria-label={t('lobby.customTitle')}>
              <Segment
                type="button"
                $active={!settings.customQuestions}
                aria-pressed={!settings.customQuestions}
                disabled={!isHost}
                onClick={() => isHost && actions.setCustomQuestions(false)}
              >
                📖 {t('lobby.customOff')}
              </Segment>
              <Segment
                type="button"
                $active={settings.customQuestions}
                aria-pressed={settings.customQuestions}
                disabled={!isHost}
                onClick={() => isHost && actions.setCustomQuestions(true)}
              >
                ✏️ {t('lobby.customOn')}
              </Segment>
            </Segmented>
            <Subtle>{t('lobby.customHint')}</Subtle>
            {settings.customQuestions}
          </Stack>
        </Stack>
      </Card>

      {isHost ? (
        <Button
          type="button"
          $tone="primary"
          $size="lg"
          $full
          disabled={!enough}
          onClick={actions.startGame}
        >
          🚀 {t('lobby.startButton')}
        </Button>
      ) : (
        <Subtle $center>{t('lobby.waitingForHost')}</Subtle>
      )}
    </>
  );
}
